/**
 * @name RuleDetailBody
 * @description The main content of the rule, consisting of RuleSection components.
 * @features
 *  - Get HTML content from Paligo via backend API
 *  - Parse HTML into ReactElements
 *  - Render RuleSection components
 *  - Handle playing audio (mp3 file) for each section
 *  - Handle internal linking to other rules or modules
 */

import ReactHtmlParser, { DOMNode } from "html-react-parser";
import React, { Dispatch, ReactElement, ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { downloadHtml } from "../../../redux/actions/RuleUnit";
import { AppState } from "../../../redux/store";
import { getMetadataObject } from "../../../utils/RuleUtil";
import { ContentRevision } from "../../../types/models/ContentRevision";
import { RuleInstance } from "../../../types/models/RuleInstance";
import RuleSection from "./RuleSection";
import RuleInstanceDetailSkeleton from "./RuleInstanceDetailSkeleton";
import "./RuleDetailBodyStyle.scss";
import { useNavigate} from "react-router-dom";
import { Element as ParsedElement } from "html-react-parser";
import { showMessage } from "../../../redux/actions";
import { RuleAudioState } from "../../../types/models/RuleAudioState";
import { getAudioFile, setAutoPlay, startAudio, stopAudio } from "../../../redux/actions/RuleAudio";

interface RuleDetailBodyProps {
  contentRevision: ContentRevision | undefined;
  ruleInstance: RuleInstance | null | undefined;
  expandAll: boolean;
  onClose?: () => void;
  onFailedLoading?: () => void;
}

interface ParsedElementWithData extends ParsedElement {
  data: string;
}

const RuleDetailBody: React.FC<RuleDetailBodyProps> = (prop) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [html, setHtml] = useState<string | undefined>(undefined);
  const [currentActiveIndex, setCurrentActiveIndex] = useState<number | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const audioState = useSelector<AppState, RuleAudioState>((state) => state.audio);

  useEffect(() => {
    setCurrentActiveIndex(audioState.currentAudioIndex);
  }, [audioState.currentAudioIndex]);

  useEffect(() => {
    if (prop.ruleInstance) {
      setIsDownloading(true);
      dispatch(
        downloadHtml(
          prop.contentRevision?.ContentRevisionId ?? "",
          prop.ruleInstance?.Filename ?? "",
          (html) => {
            if (!html && prop.onFailedLoading) {
              prop.onFailedLoading();
            }
            setHtml(html ?? "");
            setIsDownloading(false);
          }
        )
      );
    } else {
      setHtml(undefined);
    }
  }, [prop.ruleInstance]);

  /**
   * Handle Start / Stop Audio
   */
  const handleOnClickSection = (index: number) => {
    if (!prop.ruleInstance || !prop.contentRevision) return;
    if (index === currentActiveIndex && audioState.isPlaying) {
      dispatch(stopAudio());
    } else {
      dispatch(setAutoPlay(false));
      if (audioState.audioMap?.has(index)) {
        dispatch(startAudio(index));
      } else {
        dispatch(
          getAudioFile(
            prop.ruleInstance,
            prop.contentRevision.ContentRevisionId,
            index,
            (result) => {
              result ? dispatch(startAudio(index)) : dispatch(stopAudio());
            }
          )
        );
      }
    }
  };

  /**
   * @name handleInternalLink
   * @description Handles internal linking to other rules or modules.
   * @param e The mouse event that triggered the function.
   * @param options An object containing the pathname and search parameters for the link.
   */
  const handleInternalLink = (e: React.MouseEvent<HTMLAnchorElement>, fullPath: string) => {
    e.preventDefault();
    if (prop.onClose) {
      prop.onClose();
    }

    const routerPath = fullPath.substring(1);
    navigate(routerPath, { replace: true });
  };

  /**
   * @name getContentsFromHtml
   * @description Parses the HTML content into ReactElements and returns the children of the body element.
   * @param html The HTML content to parse.
   * @param dispatch The dispatch function from the Redux store.
   * @returns An array of ReactElements representing the children of the body element.
   */
  const getContentsFromHtml = (html: string, dispatch: Dispatch<unknown>) => {
    const parsedHtml = ReactHtmlParser(html, {
      replace: replaceLinks,
    }) as ReactElement;

    if (!parsedHtml) {
      loggingForHtmlAnalysing(
        html,
        "[Error] the HTML file you downloaded could not be parsed into an element.",
        dispatch
      );
      const emptyResult: ReactElement[] = [];
      return emptyResult;
    }

    const children = (parsedHtml.props?.children as ReactElement[]) ?? null;
    if (!children) {
      loggingForHtmlAnalysing(
        html,
        "[Error] the HTML file you downloaded could not be parsed into an element. No children found",
        dispatch
      );
      return [parsedHtml];
    }

    const body = children.find((child) => child.type == "body") as ReactElement;
    if (!body) {
      loggingForHtmlAnalysing(
        html,
        "[Error] the HTML file you downloaded could not be parsed into an element. No body found",
        dispatch
      );
      return children;
    }

    const bodyChildren = (body.props?.children as ReactElement[]) ?? null;
    if (!bodyChildren) {
      loggingForHtmlAnalysing(
        html,
        "[Error] the HTML file you downloaded could not be parsed into an element. No body children found",
        dispatch
      );
      return [body];
    }

    const targetSection = bodyChildren.find(
      (bodyChild) => bodyChild.props?.className == "section"
    ) as ReactElement;
    if (!targetSection) {
      loggingForHtmlAnalysing(
        html,
        "[Error] the HTML file you downloaded could not be parsed into an element. No section found",
        dispatch
      );
      return bodyChildren;
    }

    const contents = (targetSection.props.children as ReactElement[]).filter(
      (child) => child.props
    );
    if (!contents) {
      loggingForHtmlAnalysing(
        html,
        "[Error] the HTML file you downloaded could not be parsed into an element. No section contents found",
        dispatch
      );
      return [targetSection];
    }

    // If the content includes the .subtitle class, separate the content into title and subtitle to align correctly with Paragraphs and Miscellaneous by index.
    const elementList: ReactElement[] = [];
    contents.forEach((content) => {
      const subTitleElement = findChildByClassName(content, "subtitle");
      if (content.props.className == "titlepage" && subTitleElement) {
        const titleElement = findChildByClassName(content, "title");
        if (titleElement) {
          elementList.push(titleElement);
        }
        elementList.push(subTitleElement);
      } else {
        elementList.push(content);
      }
    });

    return elementList;
  };

  const loggingForHtmlAnalysing = (html: string, message: string, dispatch: Dispatch<unknown>) => {
    dispatch(showMessage(message));
  };

  function findChildByClassName(
    element: ReactElement,
    searchClassName: string
  ): ReactElement | null {
    if (!element || !element.props) return null;

    const { children, className } = element.props;

    // Check if current element has the className
    if (typeof className === "string" && className.split(" ").includes(searchClassName)) {
      return element;
    }

    // Search through the children recursively
    const searchChildren = (childNodes: ReactNode): ReactElement | null => {
      if (!childNodes) return null;
      let found: ReactElement | null = null;
      React.Children.forEach(childNodes, (child) => {
        if (found) return; // stop once found
        if (React.isValidElement(child)) {
          if (
            typeof child.props.className === "string" &&
            child.props.className.split(" ").includes(searchClassName)
          ) {
            found = child;
          } else {
            found = searchChildren(child.props.children);
          }
        }
      });
      return found;
    };
    return searchChildren(children);
  }

  /**
   * @name replaceLinks
   * @description Replaces anchor tags with react-router-dom links.
   * @param domNode
   * @returns DOMNode : Augmented anchor tag handled by react-router-dom
   */
  const replaceLinks = (domNode: DOMNode) => {
    const domElement: ParsedElement = domNode as ParsedElement;

    const ruleId = prop.ruleInstance?.RuleId;

    if (domElement.name === "a") {
      const href = domElement.attribs?.href || "";

      const hrefFileName = href.split("/").pop()?.split(".html")[0] || "";

      const isScrollLink = ruleId && (hrefFileName === ruleId || href.includes(ruleId)); 

      // Check if we're internally-linking to a rule or module
      const isModule = domElement.attribs?.class?.includes("link linktype-publication");
      const isRule = domElement.attribs?.class?.includes("link linktype-component");
      const isInternalLink = isModule || isRule;

      //Handle internal scrolling links
      if (isScrollLink && href.includes("#")) {
        let uuid = "";
        const hashPart = href.split("#")[1];
        
        if (hashPart.includes("_")) {
          // For rules with format {ruleId}.html#{ruleId}_{sectionId}
          uuid = hashPart.split("_")[1]; // Gets the second UUID
        } else {
          uuid = hashPart;
        }

        if (domElement.children && domElement.children[0]) {
          const content = domElement.children[0] as ParsedElementWithData;

          return (
            <a
              href={href}
              onClick={(e) => {
                e.preventDefault();

                let matchingElement = document.getElementById(uuid);

                if(!matchingElement) {
                  matchingElement = document.getElementById(hashPart);
                  console.warn(`No element found with ID: ${uuid} or ${hashPart}`);
                }

                if (matchingElement) {
                  matchingElement.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }}
            >
              {content.data}
            </a>
          );
        }
        else {
          return;
        }
      }

      if(!isInternalLink) return;

      let uuid = "";

      if (isModule) {
        uuid = domElement.attribs.href.split("#")[1]; // Example source: href="/document/preview/7075#UUID-33c49137-d9c4-db15-71c1-12d4e41fbb88"
      }
      if (isRule) {
        if (domElement.attribs.href.includes(".html")) {
          uuid = domElement.attribs.href.split(".html")[0]; // Example source href="UUID-537fd62c-454a-3995-d147-27809bc9f650.html";
        } else {
          uuid = domElement.attribs.href.split("#")[1]; // Example source: href="/document/preview/7075#UUID-33c49137-d9c4-db15-71c1-12d4e41fbb88"
        }
      }

      const fullPath = isModule ? `#/rules?moduleId=${uuid}` : `#/rules?ruleId=${uuid}`;

      if (domElement.children && domElement.children[0]) {
        const content = domElement.children[0] as ParsedElementWithData;

        return (
          <a
            href={fullPath}
            onClick={(e) => {
              handleInternalLink(e, fullPath);
            }}
          >
            {content.data}
          </a>
        );
      }
    }
  };

  const createSectionsFromDownloadedHTML = (html: string) => {
    if (!html) {
      return <Box></Box>;
    }

    // replace static image path
    html = html.replaceAll("../css/image/", "./css/image/");

    // replace image path to the server URL
    prop.contentRevision?.Miscellaneous?.forEach((miscellanea) => {
      const lowerFileName = miscellanea.Filename.toLowerCase();
      if (
        lowerFileName.endsWith(".jpg") ||
        lowerFileName.endsWith(".jpeg") ||
        lowerFileName.endsWith(".png")
      ) {
        html = html.replaceAll(
          miscellanea.Filename,
          `${process.env.REACT_APP_API_BASE_URL}/content/${prop.contentRevision?.ContentRevisionId}/${miscellanea.Filename}`
        );
      }
    });

    const contents = getContentsFromHtml(html, dispatch);

    let indexOffset = 0;

    const reactNodes: ReactElement[] = contents?.map((element, elementIndex) => {
      const isInformalTable =
        element.props?.className?.toString().split(" ").includes("informaltable") ?? false;

      if (isInformalTable) {
        // [KRT-1856] Each row in a table is considered a "paragraph".
        // There is code inside RuleSection to handle text-to-speech for each table row, but the
        // index for subsequent paragraphs ends up being incorrect. This is a workaround to offset
        // the index to try and compensate for the mismatch between element and paragraph indices.
        let tableRowCount = 0;

        // Look for a <table>
        React.Children.forEach(element.props?.children, (maybeTable) => {
          if (maybeTable.type === "table") {
            React.Children.forEach(maybeTable.props?.children, (tableChild) => {
              // We expect <tr>s to be inside a <tbody> or <thead>, but HTML does not require it
              switch (tableChild.type) {
                case "thead":
                case "tbody":
                  React.Children.forEach(tableChild.props?.children, (maybeRow) => {
                    if (maybeRow.type === "tr") {
                      tableRowCount += 1;
                    }
                  });
                  break;
                case "tr":
                  tableRowCount += 1;
                  break;
              }
            });
          }
        });
        if (tableRowCount !== 0) {
          // One "free" row is allowed per section, as the index for a table with only one row will
          // align with the expected paragraph index.
          indexOffset += tableRowCount - 1;
        }
      }

      const paragraphIndex = elementIndex + indexOffset;
      
      const paragraph =
        (prop.ruleInstance?.Paragraphs.length ?? 0) > paragraphIndex
          ? prop.ruleInstance?.Paragraphs[paragraphIndex]
          : undefined;
      const sectionMetadata = getMetadataObject(paragraph?.Metadata ?? "");

      if (!prop.ruleInstance) return <Box />;

      return (
        <Box key={elementIndex}>
          <RuleSection
            pageLocations={sectionMetadata?.conformance ?? []}
            pageRoles={sectionMetadata?.audience ?? []}
            contentRevisionId={prop.contentRevision?.ContentRevisionId ?? ""}
            rule={prop.ruleInstance}
            index={paragraphIndex}
            element={element}
            handleClickSection={(index) => {
              handleOnClickSection(index);
            }}
            isActive={currentActiveIndex === paragraphIndex && audioState.isPlaying}
            forceExpandByRole={prop.expandAll}
            hideTitle={
              (sectionMetadata?.audience ?? []).length > 0 ||
              (sectionMetadata?.conformance ?? []).length > 0
            }
            isInformalTable={isInformalTable}
            locationMetaData={prop.ruleInstance.Metadata}
          ></RuleSection>
        </Box>
      );
    });
    return reactNodes;
  };

  return (
    <Box>
      {isDownloading ? (
        <RuleInstanceDetailSkeleton />
      ) : (
        createSectionsFromDownloadedHTML(html ?? "")
      )}
    </Box>
  );
};

export default RuleDetailBody;
